import { isAudioContextSuspended, resumeAudioContext, onAudioContextStateChange } from './canBrowserPlayAudio';
import keyCodes from '../utils/keyCodes';

const AudioStatusIndicator = {
	toastElement: null,
	unsubscribe: null,
	autoHideTimeout: null,
	keyboardHandler: null,
	keyupHandler: null,
	isShowingSuccess: false,
	previousState: null,
	suppressNextKeyup: false,

	init: function() {
		// Don't initialize if already initialized
		if (this.unsubscribe) {
			return;
		}

		// Create toast element if it doesn't exist
		this.createToastElement();

		// Setup keyboard handler for remote control support
		this.setupKeyboardHandler();

		// Subscribe to state changes
		this.unsubscribe = onAudioContextStateChange((state) => {
			this.updateVisibility(state);
		});

		// Track initial state without showing toast — the toast will only
		// appear when a notification actually tries to play sound
		this.trackState();
	},

	createToastElement: function() {
		let $toast = $('#audio-status-toast');
		if ($toast.length === 0) {
			const iconClass = 'exclamation-circle-outline';
			$toast = $('<div id="audio-status-toast" style="display: none;">' +
				'<div class="audio-status-toast-content">' +
				'<div class="audio-status-toast-icon">' +
				'<span class="icon icon-' + iconClass + '"></span></div>' +
				'<div class="audio-status-toast-text">' +
				'<p class="audio-status-toast-title">Audio context suspended</p>' +
				'<p class="audio-status-toast-message">' +
				'Click here or press Enter to resume audio playback</p>' +
				'</div>' +
				'<button class="audio-status-toast-close" aria-label="Close">' +
				'<span class="icon icon-x"></span></button>' +
				'</div>' +
				'</div>');

			$('body').append($toast);

			$toast.on('click', '.audio-status-toast-content', (e) => {
				if ($(e.target).closest('.audio-status-toast-close').length || this.isShowingSuccess) {
					return;
				}
				this.resumeAudio();
			});

			$toast.on('click', '.audio-status-toast-close', (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.hide();
			});
		}

		this.toastElement = $toast;
	},

	setupKeyboardHandler: function() {
		this.suppressNextKeyup = false;

		this.keyboardHandler = (e) => {
			if (!this.toastElement || !this.toastElement.is(':visible') || this.isShowingSuccess) {
				return;
			}

			const key = e.key || e.keyCode || e.which;
			const isEnter = key === 'Enter' || key === keyCodes.enter;
			const isSpace = key === ' ' || key === keyCodes.space;
			const isEscape = key === 'Escape' || key === 27; // eslint-disable-line no-magic-numbers

			if (isEnter || isSpace) {
				this.suppressNextKeyup = true;
				e.preventDefault();
				e.stopPropagation();
				if (e.stopImmediatePropagation) e.stopImmediatePropagation();
				this.resumeAudio();
			} else if (isEscape) {
				this.suppressNextKeyup = true;
				e.preventDefault();
				e.stopPropagation();
				if (e.stopImmediatePropagation) e.stopImmediatePropagation();
				this.hide();
			}
		};

		this.keyupHandler = (e) => {
			if (!this.toastElement || !this.toastElement.is(':visible')) {
				this.suppressNextKeyup = false;
				return;
			}

			if (!this.suppressNextKeyup) {
				return;
			}

			const key = e.key || e.keyCode || e.which;
			const isEnter = key === 'Enter' || key === keyCodes.enter;
			const isSpace = key === ' ' || key === keyCodes.space;
			const isEscape = key === 'Escape' || key === 27; // eslint-disable-line no-magic-numbers

			if (isEnter || isSpace || isEscape) {
				e.preventDefault();
				e.stopPropagation();
				if (e.stopImmediatePropagation) e.stopImmediatePropagation();
				this.suppressNextKeyup = false;
			}
		};

		$(document).on('keydown.audioStatusToast', this.keyboardHandler);
		$(document).on('keyup.audioStatusToast', this.keyupHandler);
	},

	resumeAudio: function() {
		if (isAudioContextSuspended()) {
			resumeAudioContext().catch((err) => {
				console.error('Failed to resume audio:', err);
			});
		}
	},

	checkState: function() {
		const currentState = isAudioContextSuspended() ? 'suspended' : 'running';
		this.previousState = currentState;

		if (isAudioContextSuspended()) {
			this.show();
		} else {
			this.hide();
		}
	},

	trackState: function() {
		this.previousState = isAudioContextSuspended() ? 'suspended' : 'running';
	},

	updateVisibility: function(state) {
		const wasJustResumed = this.previousState === 'suspended' && state === 'running';
		this.previousState = state;

		if (state === 'suspended') {
			// Don't show toast proactively — only show when a notification
			// actually tries to play sound (via checkState in soundExecutor)
		} else if (wasJustResumed) {
			this.showSuccess();
		} else {
			this.hide();
		}
	},

	show: function() {
		if (!this.toastElement) {
			this.createToastElement();
		}

		this.isShowingSuccess = false;

		const $content = this.toastElement.find('.audio-status-toast-content');
		$content.removeClass('success').addClass('error');
		$content.css('cursor', 'pointer');

		this.toastElement
			.find('.audio-status-toast-icon .icon')
			.removeClass('icon-check-circle')
			.addClass('icon-exclamation-circle-outline');

		this.toastElement.find('.audio-status-toast-title')
			.text('Audio context suspended');

		this.toastElement.find('.audio-status-toast-message')
			.text('Click here or press Enter to resume audio playback');

		this.toastElement.find('.audio-status-toast-close').show();

		if (this.autoHideTimeout) {
			clearTimeout(this.autoHideTimeout);
		}

		const fadeDuration = 300;
		this.toastElement.fadeIn(fadeDuration);

		this.autoHideTimeout = setTimeout(() => {
			if (isAudioContextSuspended()) {
				this.toastElement.fadeOut(fadeDuration);
			}
		}, 30000); // eslint-disable-line no-magic-numbers
	},

	showSuccess: function() {
		if (!this.toastElement) {
			this.createToastElement();
		}

		this.isShowingSuccess = true;

		if (this.autoHideTimeout) {
			clearTimeout(this.autoHideTimeout);
		}

		const $content = this.toastElement.find('.audio-status-toast-content');
		$content.removeClass('error').addClass('success');
		$content.css('cursor', 'default');

		this.toastElement
			.find('.audio-status-toast-icon .icon')
			.removeClass('icon-exclamation-circle-outline')
			.addClass('icon-check-circle');

		this.toastElement.find('.audio-status-toast-title')
			.text('Audio resumed successfully');

		this.toastElement.find('.audio-status-toast-message')
			.text('Browser audio is now active and working');

		this.toastElement.find('.audio-status-toast-close').hide();

		const fadeDuration = 300;
		if (!this.toastElement.is(':visible')) {
			this.toastElement.fadeIn(fadeDuration);
		}

		this.autoHideTimeout = setTimeout(() => {
			this.toastElement.fadeOut(fadeDuration, () => {
				this.isShowingSuccess = false;
			});
		}, 3000); // eslint-disable-line no-magic-numbers
	},

	hide: function() {
		if (!this.toastElement) {
			return;
		}

		this.isShowingSuccess = false;

		if (this.autoHideTimeout) {
			clearTimeout(this.autoHideTimeout);
			this.autoHideTimeout = null;
		}

		this.toastElement.fadeOut(300); // eslint-disable-line no-magic-numbers
	},

	destroy: function() {
		if (this.unsubscribe) {
			this.unsubscribe();
			this.unsubscribe = null;
		}
		if (this.keyboardHandler) {
			$(document).off('keydown.audioStatusToast', this.keyboardHandler);
			this.keyboardHandler = null;
		}
		if (this.keyupHandler) {
			$(document).off('keyup.audioStatusToast', this.keyupHandler);
			this.keyupHandler = null;
		}
		if (this.autoHideTimeout) {
			clearTimeout(this.autoHideTimeout);
			this.autoHideTimeout = null;
		}
		this.hide();
		if (this.toastElement) {
			this.toastElement.remove();
			this.toastElement = null;
		}
	},
};

export default AudioStatusIndicator;
